import {
  ALERT_ERROR,
  CREATE_ANNOTATION_ERROR,
  CREATE_ANNOTATION_PENDING,
  CREATE_ANNOTATION_SUCCESS,
  CREATE_ANNOTATION_FORBIDDEN,
  DELETE_ANNOTATION_ERROR,
  DELETE_ANNOTATION_SUCCESS,
  DELETE_ROOT_COMMENT_SUCCESS,
  DESELECT_ANNOTATION,
  GENERATE_MERGED_PDF_DONE,
  GENERATE_MERGED_PDF_ERROR,
  GENERATE_MERGED_PDF_PENDING,
  GENERATE_MERGED_PDF_SUCCESSFUL,
  GET_ANNOTATIONS_ERROR,
  GET_ANNOTATIONS_PENDING,
  GET_ANNOTATIONS_SUCCESSFUL,
  HOVER_ANNOTATION,
  SELECT_ANNOTATION,
  STAGE_ANNOTATION,
  UPDATE_ANNOTATION_PENDING,
  UPDATE_ANNOTATION_SUCCESS,
  UPDATE_ANNOTATION_ERROR,
} from './constants'

import { generateMergedPdfDownloadUrl } from './merge-utils'
import { transformAnnotationSave, deleteAnnotationFromSession } from './utils'
import { getAccessTokenFromAppState } from '../../utils'
import formatMessage from 'format-message'
import { saveAnnotationToSession } from '../draw/utils'
import { applyLegacyRotations }  from '../document/action-creators'
import groupBy from 'lodash/groupBy'

const sessionPathV1 = '/1/sessions'
const sessionPath2018_03_07 = '/2018-03-07/sessions'
const sessionPath2018_04_06 = '/2018-04-06/sessions'

export function selectAnnotation(annotationId) {
  return (dispatch) => {
    dispatch({
      type: SELECT_ANNOTATION,
      annotationId,
    })
  }
}

export function deselectAnnotation(annotationId) {
  return (dispatch) => {
    dispatch({
      type: DESELECT_ANNOTATION,
      annotationId,
    })
  }
}

export function hoverAnnotation(annotationId) {
  return (dispatch) => {
    dispatch({
      type: HOVER_ANNOTATION,
      annotationId,
    })
  }
}

export function createAnnotation(height, scale, annotation, isStaged = false) {
  const transformedAnnotation = transformAnnotationSave(height, scale, annotation)

  if (isStaged && annotation.type === 'ink') {
    saveAnnotationToSession(transformedAnnotation)
  }

  return (dispatch, getState) => {
    // Prevent annotation creation on client-rotated pages
    const state = getState()
    const pageNumber = annotation.page
    const isClientRotated = state.document.clientRotatedPages[pageNumber]

    if (isClientRotated) {
      // Prevent annotation creation on client-rotated pages
      return
    }

    const requiresComment = ['freetext'].includes(annotation.type)
    const requiresRootComment = ['commentReply'].includes(annotation.type)
    const isMissingRequiredRootComment = !annotation.inreplyto && requiresRootComment
    const isMissingRequiredComment = !transformedAnnotation.contents && requiresComment
    if (isMissingRequiredComment || isMissingRequiredRootComment || isStaged) {
      dispatch({
        type: STAGE_ANNOTATION,
        annotation: transformedAnnotation,
      })
      return
    }
    dispatch({
      type: CREATE_ANNOTATION_PENDING,
      annotation: transformedAnnotation,
    })
    return fetch(
      `${sessionPath2018_03_07}/${getAccessTokenFromAppState()}/annotations/${annotation.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transformedAnnotation),
      },
    )
      .then((response) => {
        if (response.ok) {
          return response.json()
        } else {
          if (response.status === 403) {
            throw new Error('no longer has permission to annotate')
          } else {
            return response.json().then((responseBody) => {
              throw new Error(responseBody.error)
            })
          }
        }
      })
      .then((annotation) => {
        dispatch({
          type: CREATE_ANNOTATION_SUCCESS,
          annotation,
        })
        deleteAnnotationFromSession(getAccessTokenFromAppState(), annotation.id)
        if (['ink', 'strikeout', 'highlight'].includes(annotation.type)) {
          document.querySelector(`#Annotation-${annotation.id} .Comment-create`).focus()
        }
      })
      .catch((error) => {
        if (transformedAnnotation.type === 'commentReply') {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage(
              'The comment you are replying to is not available anymore, please refresh the page.',
            ),
            show: true,
          })
        } else if (error.message === 'no longer has permission to annotate') {
          dispatch({
            type: CREATE_ANNOTATION_FORBIDDEN,
            show: true,
            error,
          })
        } else if (error.message && error.message.includes('Failed to fetch')) {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage(
              'There was a network error. This annotation could not be saved.',
            ),
            show: true,
          })
        } else {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage('There was an error saving this annotation.'),
            show: true,
          })
        }

        dispatch({
          type: CREATE_ANNOTATION_ERROR,
          annotation: transformedAnnotation,
          error,
        })
        console.error(error)
        throw error
      })
  }
}

export function updateAnnotation(height, scale, annotation, staged = false) {
  const transformedAnnotation = transformAnnotationSave(height, scale, annotation)

  if (staged && annotation.type === 'ink') {
    saveAnnotationToSession(transformedAnnotation)
  }

  return (dispatch, _getState) => {
    const requiresComment = ['freetext'].includes(annotation.type)
    const isMissingRequiredComment = !transformedAnnotation.contents && requiresComment
    if (isMissingRequiredComment || staged) {
      dispatch({
        type: STAGE_ANNOTATION,
        annotation: transformedAnnotation,
      })
      return
    }

    dispatch({
      type: UPDATE_ANNOTATION_PENDING,
      annotation: transformedAnnotation,
    })
    return fetch(
      `${sessionPath2018_03_07}/${getAccessTokenFromAppState()}/annotations/${annotation.id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(transformedAnnotation),
      },
    )
      .then((response) => {
        if (response.ok) {
          dispatch({
            type: UPDATE_ANNOTATION_SUCCESS,
            annotation: transformedAnnotation,
          })
        } else {
          throw new Error('Error updating annotation')
        }
      })
      .catch((error) => {
        dispatch({
          type: UPDATE_ANNOTATION_ERROR,
          annotation: transformedAnnotation,
          error,
        })
        if (transformedAnnotation.type === 'commentReply') {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage(
              'The comment you are replying to is not available anymore, please refresh the page.',
            ),
            show: true,
          })
        } else if (error.message && error.message.includes('Failed to fetch')) {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage(
              'There was a network error. This annotation could not be saved.',
            ),
            show: true,
          })
        } else {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage('There was an error saving this annotation.'),
            show: true,
          })
        }

        console.error(error)
        throw error
      })
  }
}

export function deleteAnnotation(annotationPage, annotationId, isRootComment = false) {
  return (dispatch, getState) => {
    const state = getState()
    const savedAnnotation =
      state.annotations &&
      state.annotations.data &&
      state.annotations.data.find((annotation) => {
        return annotation.id === annotationId
      })

    const reducerType = isRootComment ? DELETE_ROOT_COMMENT_SUCCESS : DELETE_ANNOTATION_SUCCESS

    const baseAnnotationId = !isRootComment
      ? null
      : state.annotations.stagedByPage[annotationPage].find((annotation) => {
          return annotation.id === annotationId
        }).inreplyto

    let successAction = { type: reducerType, annotationId, annotationPage }
    if (isRootComment) {
      successAction = { ...successAction, baseAnnotationId }
    }

    if (!savedAnnotation) {
      deleteAnnotationFromSession(getAccessTokenFromAppState(), annotationId)
      return dispatch(successAction)
    }
    fetch(`${sessionPathV1}/${getAccessTokenFromAppState()}/annotations/${annotationId}`, {
      method: 'DELETE',
    })
      .then((response) => {
        if (response.ok) {
          deleteAnnotationFromSession(getAccessTokenFromAppState(), annotationId)
          return dispatch(successAction)
        }
        throw new Error('Error deleting annotation')
      })
      .catch((error) => {
        // TODO: revert annotation to previous state
        if (error.message && error.message.includes('Failed to fetch')) {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage(
              'There was a network error. This annotation could not be deleted.',
            ),
            show: true,
          })
        } else {
          dispatch({
            type: ALERT_ERROR,
            message: formatMessage('There was an error. It was not deleted.'),
            show: true,
          })
        }
        dispatch({
          type: DELETE_ANNOTATION_ERROR,
          error,
        })
      })
  }
}

export function loadAnnotations(_documentUrl, _apiVersion = '1') {
  return (dispatch, _getState) => {
    dispatch({ type: GET_ANNOTATIONS_PENDING })

    fetch(`${sessionPath2018_04_06}/${getAccessTokenFromAppState()}/annotations`, {
      headers: {
        'Cache-control': 'no-cache',
        Pragma: 'no-cache',
        Expires: 0,
      },
    })
      .then((response) => {
        if (response.ok) {
          return response.json()
        }
        throw new Error('Error fetching annotations')
      })
      .then((response) => {
        dispatch({
          type: GET_ANNOTATIONS_SUCCESSFUL,
          response,
          sessionId: getAccessTokenFromAppState(),
        })
        // After annotations load, apply legacy rotations only to pages with annotations
        const annotations = response.data || []
        const annotationsByPage = groupBy(annotations, (annotation) => annotation.page)
        const legacyRotations = window.DocViewer?.document?.rotations || {}
        dispatch(applyLegacyRotations(annotationsByPage, legacyRotations))
      })
      .catch((error) => {
        dispatch({
          type: GET_ANNOTATIONS_ERROR,
          error,
        })
      })
  }
}

export function generateMergedPdf(_documentUrl) {
  return async (dispatch, _getState) => {
    try {
      dispatch({ type: GENERATE_MERGED_PDF_PENDING })

      const downloadUrl = await generateMergedPdfDownloadUrl({
        onMergeSuccessful: () => dispatch({ type: GENERATE_MERGED_PDF_SUCCESSFUL }),
      })

      dispatch({ type: GENERATE_MERGED_PDF_DONE })
      window.location = downloadUrl
    } catch (error) {
      console.error('unexpected error generating the annotated PDF', error)
      dispatch({
        type: ALERT_ERROR,
        message: formatMessage(
          'Something went wrong generating the annotated PDF. Please try again later.',
        ),
        show: true,
      })
      dispatch({
        type: GENERATE_MERGED_PDF_ERROR,
        error,
      })
    }
  }
}

export function dismissGraderLimitModal() {
  return (dispatch) => {
    dispatch({
      type: CREATE_ANNOTATION_FORBIDDEN,
      show: false,
    })
  }
}

export function dismissAnnotationErrorModal() {
  return (dispatch) => {
    dispatch({
      type: ALERT_ERROR,
      message: '',
      show: false,
    })
  }
}
